import IconMail from "assets/icons/Mail";
import IconPhone from "assets/icons/Phone";
import IconPin from "assets/icons/Pin";
import Button from "components/Button";
import { TextAreaInput, TextInput } from "components/Input";
import Section from "components/Section";
import Title from "components/Title";
import { motion } from "framer-motion";
import React, { useCallback, useState } from "react";
import { framerMotionAnimation } from "utils/animation";
import constants from "utils/constants";
import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import styles from "./styles.module.scss";

const Contact: React.FC = () => {
  const [data, setData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [error, setError] = useState("");

  const handleChange = useCallback((key: string, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setError("");
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (Object.values(data).some((value) => !value)) {
        return setError("Please complete all fields.");
      }

      if (!isEmail(data.email)) {
        return setError("Please enter a valid email.");
      }

      let phoneNumber = data.phone;
      if (phoneNumber.startsWith("09")) {
        phoneNumber = "+63" + phoneNumber.slice(1);
      } else if (!phoneNumber.startsWith("+")) {
        phoneNumber = "+" + phoneNumber;
      }

      if (!isMobilePhone(phoneNumber)) {
        return setError("Please enter a valid phone number.");
      }

      setError("");

      const subject = encodeURIComponent("Renewable Philippines");
      const body = encodeURIComponent(
        `Hi Renewable PH,\n\n${data.message}\n\nName: ${data.name}\nCompany: ${data.company}\nEmail: ${data.email}\nPhone: ${phoneNumber}\n`
      );

      const link = `mailto:${constants.email}?subject=${subject}&body=${body}`;
      window.location.href = link;
    },
    [data]
  );

  return (
    <Section
      className={styles.container}
      contentClassName={styles.content}
      id="contact"
      label="Contact Us"
      labelClassName={styles.label}
      labelType="secondary"
    >
      <div className={styles.left}>
        <Title
          titleClassName={styles.title}
          title="<highlight>Got a Project?</highlight> Let's Connect!"
          type="secondary"
          subtitleClassName={styles.subtitle}
          subtitle="Turn your vision into a sustainable, energy-efficient reality with our expert solutions and seamless project execution! Send us a message and let’s bring your ideas to life!"
        />

        <div className={styles.buttons}>
          <motion.div {...framerMotionAnimation}>
            <Button
              className={styles.contactButton}
              href={`mailto:${constants.email}`}
            >
              <div className={styles.icon}>
                <IconMail />
              </div>
              {constants.email}
            </Button>
          </motion.div>

          <motion.div {...framerMotionAnimation}>
            <Button
              className={styles.contactButton}
              href={`tel:${constants.phone}`}
            >
              <div className={styles.icon}>
                <IconPhone />
              </div>
              {constants.phone}
            </Button>
          </motion.div>

          <motion.div {...framerMotionAnimation}>
            <Button
              className={styles.contactButton}
              href="https://maps.app.goo.gl/cRSbbBT5X4oTLnMRA"
              target="_blank"
            >
              <div className={styles.icon}>
                <IconPin />
              </div>
              {constants.address}
            </Button>
          </motion.div>
        </div>
      </div>

      <motion.form
        onSubmit={handleSubmit}
        className={styles.right}
        {...framerMotionAnimation}
      >
        <div className={styles.row}>
          <TextInput
            label="Name"
            onChange={(value) => handleChange("name", value)}
            placeholder="Juan Dela Cruz"
            value={data.name}
          />

          <TextInput
            label="Company"
            onChange={(value) => handleChange("company", value)}
            placeholder="Facebook"
            value={data.company}
          />
        </div>

        <div className={styles.row}>
          <TextInput
            label="Email"
            onChange={(value) => handleChange("email", value)}
            placeholder="example@company.com"
            value={data.email}
          />

          <TextInput
            label="Phone"
            onChange={(value) => handleChange("phone", value)}
            placeholder="+639123456789"
            value={data.phone}
          />
        </div>

        <TextAreaInput
          label="Message"
          onChange={(value) => handleChange("message", value)}
          placeholder="Leave us a message or ask any questions..."
          value={data.message}
        />

        <Button className={styles.sendButton} isSubmit type="primary">
          Send
        </Button>

        <div className={styles.error}>{error}</div>
      </motion.form>
    </Section>
  );
};

export default Contact;
